'use client';

import { useEffect, useRef } from 'react';

import type { FlagExperiment } from 'src/data/FlagsApi/FlagsApi.type';
import type { ABTestEventType } from 'src/general/helpers/CarmaTrackingApi.types';

import { sendABTestEvent } from './sendABTestEvent';

type UseABTestEventOptions = {
  /** Whether the event should be sent (useful for conditional events) */
  enabled?: boolean;
};

/**
 * Custom hook for sending A/B test events in a declarative way
 *
 * @param experiment - The experiment to track
 * @param type - The type of event to track
 * @param options - Configuration options
 *
 * @example
 * ```tsx
 * // Basic usage
 * useABTestEvent(experiment, 'impression');
 *
 * // With options
 * useABTestEvent(experiment, 'conversion_0', {
 *   enabled: userHasCompletedAction
 * });
 * ```
 */
export const useABTestEvent = (
  experiment: FlagExperiment | null,
  type: ABTestEventType,
  options: UseABTestEventOptions = {},
) => {
  const { enabled = true } = options;
  const hasSentRef = useRef<string>('');

  useEffect(() => {
    // Don't send if no experiment or disabled
    if (!experiment || !enabled) {
      return;
    }
    const refId = generateExperimentRefId(experiment, type);
    if (hasSentRef.current === refId) {
      return;
    }

    sendABTestEvent(experiment, type);
    hasSentRef.current = refId;
  }, [experiment?.name, type, enabled]);

  // Return a manual trigger function for cases where you need more control
  const triggerEvent = () => {
    if (experiment && !enabled && hasSentRef.current !== generateExperimentRefId(experiment, type)) {
      sendABTestEvent(experiment, type);
      hasSentRef.current = generateExperimentRefId(experiment, type);
    }
  };

  return { triggerEvent };
};

export const generateExperimentRefId = (experiment: FlagExperiment, type: ABTestEventType) => {
  return `${experiment.name}-${type}`;
};
